"use client";

import { AppDownloadCTA } from "@faire/retailer-visitor-shared/components/Footer/AppDownloadCTA";
import { Footer } from "@faire/retailer-visitor-shared/components/Footer/index";
import { OptInNudgeFooterBanner } from "@faire/retailer-visitor-shared/components/Footer/OptInNudgeFooterBanner";
import { RetailerToMakerReferral } from "@faire/retailer-visitor-shared/components/Footer/RetailerToMakerReferral";
import { POSCohortFooterBanner } from "@faire/retailer-visitor-shared/features/PointOfSale/components/POSCohortFooterBanner";
import { usePOSSummary } from "@faire/retailer-visitor-shared/features/PointOfSale/hooks/usePOSSummary";
import { useIsUnsubscribedToMarketingEmails } from "@faire/retailer-visitor-shared/hooks/retailer/useIsUnsubscribedToMarketingEmails";
import { useRetailerStatus } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerStatus";
import { useRetailerType } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailerType";
import { useShowContextualAppDownloadCTAs } from "@faire/retailer-visitor-shared/hooks/retailer/useShowContextualAppDownloadCTAs";
import { isLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { nextValue } from "@faire/retailer-visitor-shared/lib/nextValue";
import { getSettingEnableOptInNudge } from "@faire/retailer-visitor-shared/settings/getSettingEnableOptInNudge";
import { useMilestone } from "@faire/retailer-visitor-shared/stores/domain/useMilestone";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { POS_MARKETING_BANNERS_DISMISSED } from "@faire/web-api--source/indigofair/data/IUserMilestone/RetailerMilestone/POS_MARKETING_BANNERS_DISMISSED";
import { IRetailerLinkCohortEnum } from "@faire/web-api--source/indigofair/retailers/pos/IRetailerLinkCohortEnum";
import { observer } from "mobx-react";
import * as React from "react";

enum FooterBannerContent {
  NONE,
  R2B_REFERRAL,
  POS_COHORT,
  APP_DOWNLOAD,
  OPT_IN_NUDGE,
}

const getFooterBannerContent = (
  hideReferral: boolean,
  retailerLinkCohort: IRetailerLinkCohortEnum.RetailerLinkCohort,
  hasDismissedPosBanner: boolean,
  retailerInfo: {
    showR2B: boolean;
    isNewUser: boolean;
    showContextualAppDownloadCTAs: boolean;
    isUnsubscribedToMarketingEmails: boolean;
  }
): FooterBannerContent => {
  // Logged out user sees nothing
  if (!isLoggedInRetailer()) {
    return FooterBannerContent.NONE;
  }

  const {
    showR2B,
    isNewUser,
    showContextualAppDownloadCTAs,
    isUnsubscribedToMarketingEmails,
  } = retailerInfo;
  const shouldShowOptInNudgeBanner =
    isUnsubscribedToMarketingEmails && getSettingEnableOptInNudge();
  const shouldShowPOSCohortBanner =
    SUPPORTED_POS_BANNER_COHORTS.includes(retailerLinkCohort) &&
    (!hasDismissedPosBanner ||
      retailerLinkCohort ===
        IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE);

  // We don't display any advanced features for new retailers
  if (isNewUser) {
    return FooterBannerContent.NONE;
  }

  const possibleBanners = [];

  // Should show R2B
  if (!hideReferral && showR2B) {
    possibleBanners.push(FooterBannerContent.R2B_REFERRAL);
  }

  // Should show app download banner
  if (showContextualAppDownloadCTAs) {
    possibleBanners.push(FooterBannerContent.APP_DOWNLOAD);
  }

  // Should show opt in nudge banner
  if (shouldShowOptInNudgeBanner) {
    possibleBanners.push(FooterBannerContent.OPT_IN_NUDGE);
  }

  if (shouldShowPOSCohortBanner) {
    possibleBanners.push(FooterBannerContent.POS_COHORT);
  }

  if (possibleBanners.length === 0) {
    return FooterBannerContent.NONE;
  }
  return (
    nextValue(possibleBanners, FOOTER_BANNERS_SEEN_KEY) ??
    FooterBannerContent.NONE
  );
};

interface IProps {
  className?: string;
  hideReferral?: boolean;
  noTopBorder?: boolean;
  noTopMargin?: boolean;
}

const RetailerFooterBase: React.FC<IProps> = (props) => {
  const isSSR = useIsSSR();
  const { retailerLinkCohort } = usePOSSummary();
  const { hasOccurred: hasDismissedPosBanner } = useMilestone(
    POS_MARKETING_BANNERS_DISMISSED
  );
  const { isNewUser } = useRetailerType();
  const { showR2B } = useRetailerStatus();

  const { showContextualAppDownloadCTAs } = useShowContextualAppDownloadCTAs();
  const { isUnsubscribedToMarketingEmails } =
    useIsUnsubscribedToMarketingEmails();

  const banner = (() => {
    if (isSSR) {
      // TODO(jocelyn): getFooterBannerContent has a race condition which breaks SSR. Investigate.
      return <React.Fragment />;
    }

    switch (
      getFooterBannerContent(
        props.hideReferral ?? false,
        retailerLinkCohort,
        hasDismissedPosBanner,
        {
          showR2B,
          isNewUser,
          showContextualAppDownloadCTAs,
          isUnsubscribedToMarketingEmails,
        }
      )
    ) {
      case FooterBannerContent.R2B_REFERRAL:
        return <RetailerToMakerReferral />;
      case FooterBannerContent.POS_COHORT:
        return <POSCohortFooterBanner />;
      case FooterBannerContent.APP_DOWNLOAD:
        return <AppDownloadCTA />;
      case FooterBannerContent.OPT_IN_NUDGE:
        return <OptInNudgeFooterBanner />;
      case FooterBannerContent.NONE:
      default:
        return null;
    }
  })();

  return (
    <Footer
      className={props.className}
      noTopBorder={props.noTopBorder}
      noTopMargin={props.noTopMargin}
      hideReferral={props.hideReferral}
      showRetailerFooterForNewFooterXP
    >
      {banner}
    </Footer>
  );
};

export const RetailerFooter = observer(RetailerFooterBase);

export const FOOTER_BANNERS_SEEN_KEY = "FOOTER_BANNERS_SEEN";

const SUPPORTED_POS_BANNER_COHORTS = [
  IRetailerLinkCohortEnum.RetailerLinkCohort.NEW,
  IRetailerLinkCohortEnum.RetailerLinkCohort.CHURNED,
  IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE,
];
